import { useState } from "react";
import { Link } from "react-router-dom";
import { computeVPD } from "../utility/vpd.js";
import GrowPhaseBar from "./GrowPhaseBar.jsx";
import StabilityScore from "./StabilityScore.jsx";
import { useUpdateGrow, useDeleteGrow, useStabilityScore } from "../hooks/useGrowsQuery.js";

function vpdColor(vpd) {
  if (vpd == null) return "text-gray-400";
  if (vpd < 0.1) return "text-blue-700";
  if (vpd < 0.5) return "text-green-600";
  if (vpd < 0.9) return "text-yellow-500";
  return "text-red-600";
}

function vpdDot(vpd) {
  if (vpd == null) return "bg-gray-300";
  if (vpd < 0.1) return "bg-blue-500";
  if (vpd < 0.5) return "bg-green-500";
  if (vpd < 0.9) return "bg-yellow-400";
  return "bg-red-500";
}

const statusColors = {
  active:    "bg-green-100 text-green-800",
  completed: "bg-gray-100 text-gray-600",
  abandoned: "bg-red-100 text-red-700",
};

export default function GrowCard({ grow, liveDevice, hostname }) {
  const currentPhase = grow.current_phase;
  const deviceLabel = grow.device_display_name ?? grow.device_hostname ?? "No device";

  const updateGrow = useUpdateGrow(grow.id);
  const deleteGrow = useDeleteGrow();
  const stabilityScore = useStabilityScore({ hostname, currentPhase });

  const [renaming, setRenaming] = useState(false);
  const [nameInput, setNameInput] = useState(grow.name);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState(null);

  let tempF = null;
  let rh = null;
  if (liveDevice) {
    for (const sensor of liveDevice.sensors ?? []) {
      for (const metric of sensor.metrics ?? []) {
        if (metric.metric === "temperature" && metric.latest_value != null) tempF = metric.latest_value;
        if (metric.metric === "humidity" && metric.latest_value != null) rh = metric.latest_value;
      }
    }
  }
  const vpd = tempF != null && rh != null ? computeVPD(tempF, rh) : null;

  async function handleRename(e) {
    e.preventDefault();
    const trimmed = nameInput.trim();
    if (!trimmed || trimmed === grow.name) { setRenaming(false); return; }
    setError(null);
    try {
      await updateGrow.mutateAsync({ name: trimmed, status: grow.status });
      setRenaming(false);
    } catch {
      setError("Failed to rename");
    }
  }

  async function handleDelete() {
    setError(null);
    try {
      await deleteGrow.mutateAsync(grow.id);
    } catch {
      setError("Failed to delete");
    }
  }

  return (
    <div className="rounded-xl bg-white shadow p-4 flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          {renaming ? (
            <form onSubmit={handleRename} className="flex items-center gap-2">
              <input
                autoFocus
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                className="text-sm font-semibold border border-cyan-400 rounded px-2 py-0.5 w-full focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
              <button
                type="submit"
                disabled={updateGrow.isPending}
                className="text-xs text-white bg-cyan-600 rounded px-2 py-0.5 hover:bg-cyan-700 disabled:opacity-50 shrink-0"
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => { setRenaming(false); setNameInput(grow.name); }}
                className="text-xs text-gray-400 hover:text-gray-600 shrink-0"
              >
                Cancel
              </button>
            </form>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-cyan-800 font-semibold text-base">{grow.name}</h3>
              {grow.species && (
                <span className="text-xs bg-cyan-50 text-cyan-600 border border-cyan-200 rounded-full px-2 py-0.5">
                  {grow.species}
                </span>
              )}
              <span className={`text-xs rounded-full px-2 py-0.5 font-medium ${statusColors[grow.status] ?? statusColors.active}`}>
                {grow.status}
              </span>
            </div>
          )}
          <div className="text-xs text-gray-400 mt-0.5">{deviceLabel}</div>
        </div>
        {!renaming && (
          <div className="flex items-start gap-3 shrink-0">
            {vpd != null && (
              <div className="flex flex-col items-end">
                <div className={`flex items-center gap-1.5 text-sm font-bold tabular-nums ${vpdColor(vpd)}`}>
                  <span className={`w-2 h-2 rounded-full ${vpdDot(vpd)}`} />
                  {vpd.toFixed(2)} kPa
                </div>
                <div className="text-xs text-gray-400">VPD</div>
              </div>
            )}
            <StabilityScore score={stabilityScore} size="sm" />
          </div>
        )}
      </div>

      {/* Phase bar */}
      <GrowPhaseBar phases={grow.all_phases ?? []} />

      {/* Live readings */}
      {liveDevice && (
        <div className="flex gap-4 text-sm">
          {tempF != null && (
            <div className="text-center">
              <div className="font-bold tabular-nums text-gray-700">{tempF.toFixed(1)}<span className="text-xs font-normal">°F</span></div>
              <div className="text-xs text-gray-400">Temp</div>
            </div>
          )}
          {rh != null && (
            <div className="text-center">
              <div className="font-bold tabular-nums text-gray-700">{rh.toFixed(0)}<span className="text-xs font-normal">%</span></div>
              <div className="text-xs text-gray-400">RH</div>
            </div>
          )}
        </div>
      )}

      {/* Stats row */}
      <div className="flex gap-4 text-xs text-gray-500 border-t border-gray-50 pt-2">
        <span>{grow.total_flushes ?? 0} flush{grow.total_flushes !== 1 ? "es" : ""}</span>
        {grow.total_weight_grams > 0 && <span>{grow.total_weight_grams}g total</span>}
        <span>{grow.note_count ?? 0} note{grow.note_count !== 1 ? "s" : ""}</span>
      </div>

      {error && <div className="text-xs text-red-600">{error}</div>}

      {/* Delete confirmation */}
      {confirmDelete && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
          <p className="text-red-700 font-medium mb-1">Delete "{grow.name}"?</p>
          <p className="text-red-600 text-xs mb-3">This cannot be undone. All phases, notes, and harvest records will be permanently deleted.</p>
          <div className="flex gap-2">
            <button
              onClick={handleDelete}
              disabled={deleteGrow.isPending}
              className="text-xs bg-red-600 text-white rounded px-3 py-1.5 hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {deleteGrow.isPending ? "Deleting…" : "Yes, delete"}
            </button>
            <button
              onClick={() => setConfirmDelete(false)}
              className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1.5"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Actions */}
      {!confirmDelete && (
        <div className="flex gap-2 mt-1">
          <Link
            to={`/grows/${grow.id}`}
            className="text-xs text-cyan-600 hover:text-cyan-800 border border-cyan-200 rounded px-2 py-1 hover:bg-cyan-50 transition-colors"
          >
            View Details
          </Link>
          <button
            onClick={() => { setRenaming(true); setNameInput(grow.name); }}
            className="text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded px-2 py-1 hover:bg-gray-50 transition-colors"
          >
            Rename
          </button>
          <button
            onClick={() => setConfirmDelete(true)}
            className="text-xs text-red-400 hover:text-red-600 border border-red-100 rounded px-2 py-1 hover:bg-red-50 transition-colors ml-auto"
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
}
